import {
  JackingFieldTemplateEditor,
  JackingFieldValueEditor,
  JackingFormInstanceEditor,
  JackingSectionGroupTemplateEditor,
  JackingSectionTemplateEditor
} from '../../../../models/jacking-v2/jacking.models';

export type JackingSectionStatus = 'red' | 'orange' | 'green';
export interface JackingSectionStatusDetail {
  status: JackingSectionStatus;
  incompleteItems: string[];
}

export function getJackingSectionStatus(
  editor: JackingFormInstanceEditor,
  section: JackingSectionTemplateEditor,
  legsCount: number
): JackingSectionStatus {
  return getJackingSectionStatusDetail(editor, section, legsCount).status;
}

export function getJackingSectionStatusDetail(
  editor: JackingFormInstanceEditor,
  section: JackingSectionTemplateEditor,
  legsCount: number
): JackingSectionStatusDetail {
  if (!editor || !section) {
    return { status: 'red', incompleteItems: [] };
  }

  if (!sectionHasAnyValue(editor, section, legsCount)) {
    return { status: 'red', incompleteItems: [] };
  }

  const incompleteItems = getSectionIncompleteItems(editor, section, legsCount);
  return {
    status: incompleteItems.length === 0 ? 'green' : 'orange',
    incompleteItems
  };
}

function sectionHasAnyValue(
  editor: JackingFormInstanceEditor,
  section: JackingSectionTemplateEditor,
  legsCount: number
): boolean {
  const groups = getGroups(section);
  return groups.some(group => {
    if (group.usesFieldTemplates) {
      return getFields(group).some(field => hasFieldValue(editor, field));
    }

    return customGroupHasAnyValue(editor, group.componentKey, legsCount);
  });
}

function isSectionComplete(
  editor: JackingFormInstanceEditor,
  section: JackingSectionTemplateEditor,
  legsCount: number
): boolean {
  const groups = getGroups(section);
  if (!groups.length) {
    return false;
  }

  return groups.every(group => {
    if (group.usesFieldTemplates) {
      const fields = getFields(group);
      return fields.length > 0 && fields.every(field => isFieldComplete(editor, field));
    }

    return isCustomGroupComplete(editor, group.componentKey, legsCount);
  });
}

function getSectionIncompleteItems(
  editor: JackingFormInstanceEditor,
  section: JackingSectionTemplateEditor,
  legsCount: number
): string[] {
  const items: string[] = [];

  for (const group of getGroups(section)) {
    if (group.usesFieldTemplates) {
      for (const field of getFields(group)) {
        if (!isFieldComplete(editor, field)) {
          items.push(field.label || `Field ${field.id}`);
        }
      }
      continue;
    }

    items.push(...getCustomGroupIncompleteItems(editor, group.componentKey, legsCount));
  }

  return items;
}

function getGroups(section: JackingSectionTemplateEditor): JackingSectionGroupTemplateEditor[] {
  return (section?.groups || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
}

function getFields(group: JackingSectionGroupTemplateEditor): JackingFieldTemplateEditor[] {
  return (group?.fields || []).slice().sort((a, b) => a.order - b.order);
}

function hasFieldValue(editor: JackingFormInstanceEditor, field: JackingFieldTemplateEditor): boolean {
  if (!field || !editor) {
    return false;
  }

  if (field.dataKey) {
    return hasValue(getDataValue(editor, field.dataKey));
  }

  return hasValue(getStoredFieldValue(field, editor.valuesByFieldId?.[field.id]));
}

function isFieldComplete(editor: JackingFormInstanceEditor, field: JackingFieldTemplateEditor): boolean {
  if (!hasFieldValue(editor, field)) {
    return false;
  }

  if (!field.requiresConfirmation || field.dataKey) {
    return true;
  }

  const stored = editor.valuesByFieldId?.[field.id];
  return stored?.boolValue === true;
}

function getDataValue(editor: JackingFormInstanceEditor, key: string): any {
  if (!key || !editor?.dataByKey) {
    return null;
  }

  const exact = editor.dataByKey[key];
  if (exact !== undefined && exact !== null) {
    return exact;
  }

  const lowerKey = key.toLowerCase();
  const match = Object.keys(editor.dataByKey).find(k => k.toLowerCase() === lowerKey);
  return match ? editor.dataByKey[match] : null;
}

function getStoredFieldValue(field: JackingFieldTemplateEditor, stored?: JackingFieldValueEditor): any {
  if (!field || !stored) {
    return null;
  }

  switch (field.dataTypeId) {
    case 1:
      return stored.stringValue;
    case 2:
    case 6:
      return stored.intValue;
    case 3:
      return stored.decimalValue;
    case 4:
    case 5:
      return stored.dateTimeValue;
    case 7:
      return stored.boolValue;
    default:
      return null;
  }
}

function customGroupHasAnyValue(editor: JackingFormInstanceEditor, componentKey: string, legsCount: number): boolean {
  switch (componentKey) {
    case 'LoadCellsGrid':
      return getOrderedLegNumbers(editor, legsCount).some(legNo => getLoadCellSelections(editor, legNo).length > 0);
    case 'JackingEventsTable':
      return (editor?.jackingEvents?.eventRows || []).some(r => eventRowHasAnyValue(r));
    case 'FinalValues':
      return finalValuesHasAnyValue(editor, legsCount);
    case 'JackingLegSummaries':
      return (editor?.jackingLegSummary?.legs || []).some(l => legSummaryRowHasAnyValue(l));
    case 'Signatures':
      return signaturesHaveAnyValue(editor);
    default:
      return false;
  }
}

function isCustomGroupComplete(editor: JackingFormInstanceEditor, componentKey: string, legsCount: number): boolean {
  switch (componentKey) {
    case 'LoadCellsGrid': {
      const legs = getOrderedLegNumbers(editor, legsCount);
      return legs.length > 0 && legs.every(legNo => getLoadCellSelections(editor, legNo).length > 0);
    }
    case 'JackingEventsTable': {
      const rows = editor?.jackingEvents?.eventRows || [];
      return rows.length > 0 && rows.every(r => isEventRowComplete(editor, r, legsCount));
    }
    case 'FinalValues':
      return isFinalValuesComplete(editor, legsCount);
    case 'JackingLegSummaries': {
      const legs = getOrderedLegNumbers(editor, legsCount);
      return legs.length > 0 && legs.every(legNo => isLegSummaryLegComplete(editor, legNo));
    }
    case 'Signatures':
      return areSignaturesComplete(editor);
    default:
      return false;
  }
}

function getCustomGroupIncompleteItems(editor: JackingFormInstanceEditor, componentKey: string, legsCount: number): string[] {
  switch (componentKey) {
    case 'LoadCellsGrid': {
      return getOrderedLegNumbers(editor, legsCount)
        .filter(legNo => getLoadCellSelections(editor, legNo).length === 0)
        .map(legNo => `Load Cells - Leg ${legNo}`);
    }
    case 'JackingEventsTable': {
      const rows = editor?.jackingEvents?.eventRows || [];
      return rows.flatMap(r => getEventRowIncompleteItems(editor, r, legsCount));
    }
    case 'FinalValues':
      return getFinalValuesIncompleteItems(editor, legsCount);
    case 'JackingLegSummaries': {
      return getOrderedLegNumbers(editor, legsCount)
        .flatMap(legNo => getLegSummaryIncompleteItems(editor, legNo));
    }
    case 'Signatures':
      return getSignatureIncompleteItems(editor);
    default:
      return [];
  }
}

function getOrderedLegNumbers(editor: JackingFormInstanceEditor, legsCount: number): number[] {
  const layouts = editor?.template?.legLayouts || [];
  if (layouts.length) {
    return layouts
      .slice()
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .map(l => l.legNumber);
  }

  return Array.from({ length: legsCount || 0 }, (_, i) => i + 1);
}

function getLoadCellSelections(editor: JackingFormInstanceEditor, legNumber: number): number[] {
  const leg = editor?.loadCells?.legs?.find(l => l.legNumber === legNumber);
  return leg?.selectedCells || [];
}

function eventRowHasAnyValue(row: any): boolean {
  if (!row) {
    return false;
  }

  if (hasValue(row.eventDate)) { return true; }
  if (row.timeConfirmed === true) { return true; }
  if (hasValue(row.titleParameterValue)) { return true; }
  if (hasValue(row.singleValueDecimal) || hasValue(row.singleValueInt) || hasValue(row.singleValueText)) { return true; }
  if (row.craneBrakesLifted === true) { return true; }

  return (row.legs || []).some((leg: any) =>
    leg.legCheckboxValue === true
    || hasValue(leg.load)
    || hasValue(leg.ext)
    || hasValue(leg.penetration)
    || hasValue(leg.r)
    || hasValue(leg.p)
    || hasValue(leg.d)
    || hasValue(leg.waterDepth)
    || hasValue(leg.jettingPressure)
  );
}

function isEventRowComplete(editor: JackingFormInstanceEditor, row: any, legsCount: number): boolean {
  if (!row) {
    return false;
  }

  if (row.requiresDateTime && !hasValue(row.eventDate)) {
    return false;
  }

  if (row.requiresDateTimeConfirmation && row.timeConfirmed !== true) {
    return false;
  }

  if (row.hasTitleParameter && !hasValue(row.titleParameterValue)) {
    return false;
  }

  if (row.hasSingleValue) {
    const hasSingle = hasValue(row.singleValueDecimal) || hasValue(row.singleValueInt) || hasValue(row.singleValueText);
    if (!hasSingle) {
      return false;
    }
  }

  if (row.requiresCraneBrakesLiftedSelect && row.craneBrakesLifted !== true) {
    return false;
  }

  const orderedLegs = getOrderedLegNumbers(editor, legsCount);
  for (const legNo of orderedLegs) {
    const leg = (row.legs || []).find((l: any) => l.legNumber === legNo) || {};

    if (row.hasLegLoad && !hasValue(leg.load)) {
      return false;
    }
    if (row.hasLegExtension && !hasValue(leg.ext)) {
      return false;
    }
    if (row.hasLegPenetration && !hasValue(leg.penetration)) {
      return false;
    }
    if (row.hasRpd && !(hasValue(leg.r) && hasValue(leg.p) && hasValue(leg.d))) {
      return false;
    }
  }

  return true;
}

function getEventRowIncompleteItems(editor: JackingFormInstanceEditor, row: any, legsCount: number): string[] {
  if (!row) {
    return [];
  }

  const items: string[] = [];
  const name = row.eventName || 'Event';

  if (row.requiresDateTime && !hasValue(row.eventDate)) {
    items.push(`${name} - Time`);
  }

  if (row.requiresDateTimeConfirmation && row.timeConfirmed !== true) {
    items.push(`${name} - Time confirmation`);
  }

  if (row.hasTitleParameter && !hasValue(row.titleParameterValue)) {
    items.push(`${name} - Parameter`);
  }

  if (row.hasSingleValue) {
    const hasSingle = hasValue(row.singleValueDecimal) || hasValue(row.singleValueInt) || hasValue(row.singleValueText);
    if (!hasSingle) {
      items.push(`${name} - Value`);
    }
  }

  if (row.requiresCraneBrakesLiftedSelect && row.craneBrakesLifted !== true) {
    items.push(`${name} - Crane brakes lifted`);
  }

  const orderedLegs = getOrderedLegNumbers(editor, legsCount);
  for (const legNo of orderedLegs) {
    const leg = (row.legs || []).find((l: any) => l.legNumber === legNo) || {};

    if (row.hasLegLoad && !hasValue(leg.load)) {
      items.push(`${name} - Leg ${legNo} load`);
    }
    if (row.hasLegExtension && !hasValue(leg.ext)) {
      items.push(`${name} - Leg ${legNo} ext`);
    }
    if (row.hasLegPenetration && !hasValue(leg.penetration)) {
      items.push(`${name} - Leg ${legNo} penetration`);
    }
    if (row.hasRpd) {
      if (!hasValue(leg.r)) { items.push(`${name} - Leg ${legNo} R`); }
      if (!hasValue(leg.p)) { items.push(`${name} - Leg ${legNo} P`); }
      if (!hasValue(leg.d)) { items.push(`${name} - Leg ${legNo} D`); }
    }
  }

  return items;
}

function finalValuesHasAnyValue(editor: JackingFormInstanceEditor, legsCount: number): boolean {
  const fv: any = editor?.jackingFinalValues;
  if (!fv) {
    return false;
  }

  return blockHasAnyValue(fv.finalLegValues, (leg: any) => hasValue(leg.load) || hasValue(leg.ext))
    || blockHasAnyValue(fv.finalPenetrations, (leg: any) => hasValue(leg.penetration))
    || blockHasAnyValue(fv.differenceFromExpected, (leg: any) => hasValue(leg.difference))
    || blockHasAnyValue(fv.finalRpd, (leg: any) => hasValue(leg.r) || hasValue(leg.p) || hasValue(leg.d))
    || blockHasAnyValue(fv.finalAirGap, null, ['airGap'])
    || positionHasAnyValue(fv.position)
    || liftPumpsHasAnyValue(fv.liftPumpsUsed)
    || hasValue(fv.notes);
}

function isFinalValuesComplete(editor: JackingFormInstanceEditor, legsCount: number): boolean {
  const fv: any = editor?.jackingFinalValues;
  const template: any = editor?.template?.finalValuesTemplate;
  if (!fv || !template) {
    return false;
  }

  const checks: boolean[] = [];

  if (template.hasFinalLegValues) {
    checks.push(isTimedLegBlockComplete(editor, legsCount, fv.finalLegValues, (leg: any) => hasValue(leg.load) && hasValue(leg.ext)));
  }
  if (template.hasFinalPenetrations) {
    checks.push(isTimedLegBlockComplete(editor, legsCount, fv.finalPenetrations, (leg: any) => hasValue(leg.penetration)));
  }
  if (template.hasDifferenceFromExpected) {
    checks.push(isTimedLegBlockComplete(editor, legsCount, fv.differenceFromExpected, (leg: any) => hasValue(leg.difference)));
  }
  if (template.hasRpd) {
    checks.push(isTimedLegBlockComplete(editor, legsCount, fv.finalRpd, (leg: any) => hasValue(leg.r) && hasValue(leg.p) && hasValue(leg.d)));
  }
  if (template.hasLiftPumpsUsed || template.hasDepthDeployed) {
    checks.push(isLiftPumpsComplete(editor, legsCount, fv.liftPumpsUsed));
  }
  if (template.hasFinalAirGap) {
    checks.push(
      hasValue(fv.finalAirGap?.airGap)
      && hasValue(fv.finalAirGap?.timeUtc)
      && fv.finalAirGap?.timeConfirmed === true
    );
  }
  if (template.hasNorthingEasting) {
    checks.push(isFinalPositionComplete(editor, legsCount, fv.position));
  }

  return checks.length > 0 && checks.every(Boolean);
}

function getFinalValuesIncompleteItems(editor: JackingFormInstanceEditor, legsCount: number): string[] {
  const fv: any = editor?.jackingFinalValues;
  const template: any = editor?.template?.finalValuesTemplate;
  if (!fv || !template) {
    return [];
  }

  const items: string[] = [];

  if (template.hasFinalLegValues) {
    items.push(...getTimedLegBlockIncompleteItems(editor, legsCount, 'Final leg values', fv.finalLegValues, (leg: any) => ({
      load: hasValue(leg.load),
      ext: hasValue(leg.ext)
    })));
  }
  if (template.hasFinalPenetrations) {
    items.push(...getTimedLegBlockIncompleteItems(editor, legsCount, 'Final penetrations', fv.finalPenetrations, (leg: any) => ({
      penetration: hasValue(leg.penetration)
    })));
  }
  if (template.hasDifferenceFromExpected) {
    items.push(...getTimedLegBlockIncompleteItems(editor, legsCount, 'Difference from expected', fv.differenceFromExpected, (leg: any) => ({
      difference: hasValue(leg.difference)
    })));
  }
  if (template.hasRpd) {
    items.push(...getTimedLegBlockIncompleteItems(editor, legsCount, 'Final RPD', fv.finalRpd, (leg: any) => ({
      R: hasValue(leg.r),
      P: hasValue(leg.p),
      D: hasValue(leg.d)
    })));
  }
  if (template.hasLiftPumpsUsed || template.hasDepthDeployed) {
    if (!hasValue(fv.liftPumpsUsed?.depthDeployed)) {
      items.push('Lift Pumps Used - Depth deployed');
    }
  }
  if (template.hasFinalAirGap) {
    if (!hasValue(fv.finalAirGap?.airGap)) { items.push('Final Air Gap - Value'); }
    if (!hasValue(fv.finalAirGap?.timeUtc)) { items.push('Final Air Gap - Time'); }
    if (fv.finalAirGap?.timeConfirmed !== true) { items.push('Final Air Gap - Time confirmation'); }
  }
  if (template.hasNorthingEasting) {
    items.push(...getFinalPositionIncompleteItems(editor, legsCount, fv.position));
  }
  return items;
}

function getTimedLegBlockIncompleteItems(
  editor: JackingFormInstanceEditor,
  legsCount: number,
  label: string,
  block: any,
  evaluate: (leg: any) => { [key: string]: boolean }
): string[] {
  const items: string[] = [];
  if (!hasValue(block?.timeUtc)) {
    items.push(`${label} - Time`);
  }

  for (const legNo of getOrderedLegNumbers(editor, legsCount)) {
    const leg = (block?.legs || []).find((l: any) => l.legNumber === legNo);
    if (!leg) {
      items.push(`${label} - Leg ${legNo}`);
      continue;
    }

    const checks = evaluate(leg);
    for (const key of Object.keys(checks)) {
      if (!checks[key]) {
        items.push(`${label} - Leg ${legNo} ${key}`);
      }
    }
  }

  return items;
}

function blockHasAnyValue(block: any, legPredicate: ((leg: any) => boolean) | null = null, scalarKeys: string[] = []): boolean {
  if (!block) {
    return false;
  }

  if (hasValue(block.timeUtc)) {
    return true;
  }

  if (scalarKeys.some(key => hasValue(block[key]))) {
    return true;
  }

  if (!legPredicate) {
    return false;
  }

  return (block.legs || []).some((leg: any) => legPredicate(leg));
}

function isTimedLegBlockComplete(
  editor: JackingFormInstanceEditor,
  legsCount: number,
  block: any,
  legPredicate: (leg: any) => boolean
): boolean {
  if (!block || !hasValue(block.timeUtc)) {
    return false;
  }

  const orderedLegs = getOrderedLegNumbers(editor, legsCount);
  if (!orderedLegs.length) {
    return false;
  }

  return orderedLegs.every(legNo => {
    const leg = (block.legs || []).find((l: any) => l.legNumber === legNo);
    return !!leg && legPredicate(leg);
  });
}

function liftPumpsHasAnyValue(block: any): boolean {
  if (!block) {
    return false;
  }

  return hasValue(block.depthDeployed) || (block.legs || []).some((leg: any) => leg.liftPumpUsed === true);
}

function isLiftPumpsComplete(editor: JackingFormInstanceEditor, legsCount: number, block: any): boolean {
  if (!block) {
    return false;
  }

  const orderedLegs = getOrderedLegNumbers(editor, legsCount);
  return hasValue(block.depthDeployed)
    && orderedLegs.length > 0
    && orderedLegs.every(legNo => {
      const leg = (block.legs || []).find((l: any) => l.legNumber === legNo);
      return !!leg && leg.liftPumpUsed === true;
    });
}

function positionHasAnyValue(position: any): boolean {
  if (!position) {
    return false;
  }

  return hasValue(position.northingCrpPosition)
    || hasValue(position.eastingCrpPosition)
    || hasValue(position.headingT)
    || hasValue(position.requiredDistanceToAsset)
    || hasValue(position.actualDistanceToAsset)
    || (position.legs || []).some((leg: any) => hasValue(leg.northing) || hasValue(leg.easting));
}

function isFinalPositionComplete(editor: JackingFormInstanceEditor, legsCount: number, position: any): boolean {
  if (!position) {
    return false;
  }

  const orderedLegs = getOrderedLegNumbers(editor, legsCount);
  return hasValue(position.northingCrpPosition)
    && hasValue(position.eastingCrpPosition)
    && hasValue(position.headingT)
    && hasValue(position.requiredDistanceToAsset)
    && hasValue(position.actualDistanceToAsset)
    && orderedLegs.length > 0
    && orderedLegs.every(legNo => {
      const leg = (position.legs || []).find((l: any) => l.legNumber === legNo);
      return !!leg && hasValue(leg.northing) && hasValue(leg.easting);
    });
}

function legSummaryRowHasAnyValue(leg: any): boolean {
  return hasValue(leg?.legMaxPullingLoad)
    || hasValue(leg?.legMaxPullingLoadTime)
    || hasValue(leg?.legFreeInHolesExt)
    || hasValue(leg?.legFreeInHolesExtTime)
    || hasValue(leg?.clearOfSeaBedExt)
    || hasValue(leg?.clearOfSeaBedExtTime);
}

function isLegSummaryLegComplete(editor: JackingFormInstanceEditor, legNumber: number): boolean {
  const leg = (editor?.jackingLegSummary?.legs || []).find(l => l.legNumber === legNumber);
  if (!leg) {
    return false;
  }

  return hasValue(leg.legMaxPullingLoad)
    && hasValue(leg.legMaxPullingLoadTime)
    && hasValue(leg.legFreeInHolesExt)
    && hasValue(leg.legFreeInHolesExtTime)
    && hasValue(leg.clearOfSeaBedExt)
    && hasValue(leg.clearOfSeaBedExtTime);
}

function getLegSummaryIncompleteItems(editor: JackingFormInstanceEditor, legNumber: number): string[] {
  const leg = (editor?.jackingLegSummary?.legs || []).find(l => l.legNumber === legNumber);
  const items: string[] = [];
  if (!leg) {
    return [
      `Leg Summary - Leg ${legNumber} max pulling load`,
      `Leg Summary - Leg ${legNumber} free in holes`,
      `Leg Summary - Leg ${legNumber} clear of seabed`
    ];
  }

  if (!hasValue(leg.legMaxPullingLoad)) { items.push(`Leg Summary - Leg ${legNumber} max pulling load`); }
  if (!hasValue(leg.legMaxPullingLoadTime)) { items.push(`Leg Summary - Leg ${legNumber} max pulling load time`); }
  if (!hasValue(leg.legFreeInHolesExt)) { items.push(`Leg Summary - Leg ${legNumber} free in holes ext`); }
  if (!hasValue(leg.legFreeInHolesExtTime)) { items.push(`Leg Summary - Leg ${legNumber} free in holes time`); }
  if (!hasValue(leg.clearOfSeaBedExt)) { items.push(`Leg Summary - Leg ${legNumber} clear of seabed ext`); }
  if (!hasValue(leg.clearOfSeaBedExtTime)) { items.push(`Leg Summary - Leg ${legNumber} clear of seabed time`); }
  return items;
}

function signaturesHaveAnyValue(editor: JackingFormInstanceEditor): boolean {
  const s = editor?.signatures;
  return hasValue(s?.jackingEngineer1SignerName)
    || hasValue(s?.jackingEngineer1Signature)
    || hasValue(s?.jackingEngineer2SignerName)
    || hasValue(s?.jackingEngineer2Signature);
}

function areSignaturesComplete(editor: JackingFormInstanceEditor): boolean {
  const s = editor?.signatures;
  return hasValue(s?.jackingEngineer1SignerName)
    && hasValue(s?.jackingEngineer1Signature)
    && hasValue(s?.jackingEngineer2SignerName)
    && hasValue(s?.jackingEngineer2Signature);
}

function getSignatureIncompleteItems(editor: JackingFormInstanceEditor): string[] {
  const s = editor?.signatures;
  const items: string[] = [];
  if (!hasValue(s?.jackingEngineer1SignerName)) { items.push('Signature - Jacking Engineer 1 name'); }
  if (!hasValue(s?.jackingEngineer1Signature)) { items.push('Signature - Jacking Engineer 1 signature'); }
  if (!hasValue(s?.jackingEngineer2SignerName)) { items.push('Signature - Jacking Engineer 2 name'); }
  if (!hasValue(s?.jackingEngineer2Signature)) { items.push('Signature - Jacking Engineer 2 signature'); }
  return items;
}

function getFinalPositionIncompleteItems(editor: JackingFormInstanceEditor, legsCount: number, position: any): string[] {
  const items: string[] = [];
  if (!position) {
    return ['Final Position'];
  }

  if (!hasValue(position.northingCrpPosition)) { items.push('Final Position - CRP northing'); }
  if (!hasValue(position.eastingCrpPosition)) { items.push('Final Position - CRP easting'); }
  if (!hasValue(position.headingT)) { items.push('Final Position - Heading T'); }
  if (!hasValue(position.requiredDistanceToAsset)) { items.push('Final Position - Required distance to asset'); }
  if (!hasValue(position.actualDistanceToAsset)) { items.push('Final Position - Actual distance to asset'); }

  for (const legNo of getOrderedLegNumbers(editor, legsCount)) {
    const leg = (position.legs || []).find((l: any) => l.legNumber === legNo);
    if (!leg) {
      items.push(`Final Position - Leg ${legNo}`);
      continue;
    }
    if (!hasValue(leg.northing)) { items.push(`Final Position - Leg ${legNo} northing`); }
    if (!hasValue(leg.easting)) { items.push(`Final Position - Leg ${legNo} easting`); }
  }

  return items;
}

function hasValue(value: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }

  if (typeof value === 'boolean') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value?.dateTime !== undefined) {
    return !!value.dateTime;
  }

  if (value?.isValid !== undefined) {
    return !!value.isValid;
  }

  return true;
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/helpers/jacking-section-status.helper.ts